export class ApiError extends Error {
  constructor(status, code, message, details) {
    super(message || code || `HTTP ${status}`)
    this.status = status
    this.code = code
    this.details = details
  }
}

/**
 * This tab, for the lifetime of this tab. Sent on every request so the live channel can
 * echo it back and the tab that made a change can ignore its own event — it already
 * invalidated the moment the request resolved, and refetching twice for one edit is
 * noticeable on a board being dragged around.
 */
export const CLIENT_ID =
  globalThis.crypto?.randomUUID?.() ?? `c${Math.random().toString(36).slice(2)}`

/**
 * Where to send someone back to once they have signed in again.
 *
 * Sign-in happens in a popup and <Login> renders in place at whatever URL you were on, so
 * today this is belt-and-braces — the address bar already holds the answer. It is written
 * down anyway because that is a property of the CURRENT sign-in flow, not of the app: switch
 * MSAL to loginRedirect and the page is replaced, at which point the only record of where
 * somebody was is the one we kept.
 */
const RETURN_TO_KEY = 'crew-dispatch-service:returnTo'

export function rememberReturnTo(pathWithQuery = window.location.pathname + window.location.search) {
  try {
    sessionStorage.setItem(RETURN_TO_KEY, pathWithQuery)
  } catch {
    // Private mode, or storage disabled. The URL is still the fallback.
  }
}

/** The remembered path, cleared as it is read so a later reload does not jump. */
export function takeReturnTo() {
  try {
    const v = sessionStorage.getItem(RETURN_TO_KEY)
    sessionStorage.removeItem(RETURN_TO_KEY)
    return v
  } catch {
    return null
  }
}

/**
 * Announced on the window, not through a registered callback.
 *
 * A module-level handler looks tidier and is quietly fragile: this module is imported by
 * both the app and — under Vite — by any HMR-updated copy of itself, so "the instance that
 * registered the handler" and "the instance that threw the 401" are not reliably the same
 * object. A window event has exactly one bus no matter how many module instances exist, and
 * needs no registration order to be correct.
 *
 * It also keeps this file as pure transport: it states that the session went, and takes no
 * view on what the app should do about it.
 */
export const SESSION_LOST_EVENT = 'crew-dispatch-service:session-lost'

async function request(method, path, body, opts = {}) {
  const init = {
    method,
    credentials: 'same-origin',
    headers: { 'X-Client-Id': CLIENT_ID },
    ...opts,
  }
  if (body !== undefined && !(body instanceof FormData)) {
    init.headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(body)
  } else if (body instanceof FormData) {
    init.body = body
  }

  // fetch() rejects with a bare TypeError when the request never reaches the server —
  // offline, DNS, the API restarting mid-deploy. Left alone it surfaces as "Failed to fetch"
  // (or Firefox's own wording), which reads like a bug in the app rather than a connection
  // that dropped. Converting it here means one error shape reaches every call site.
  let res
  try {
    res = await fetch(path, init)
  } catch (cause) {
    throw new ApiError(0, 'NETWORK', 'Could not reach the server', { cause: String(cause) })
  }
  if (res.status === 204) return null

  const text = await res.text()
  let data = null
  try {
    data = text ? JSON.parse(text) : null
  } catch {
    data = { raw: text }
  }

  if (!res.ok) {
    const e = data?.error || {}
    // A 401 means the session is gone, not that this particular action was refused. Without
    // this the app sat on a dead session showing "Sign in required" on every click until
    // something happened to refetch /api/me — so the fix was "reload the page", which nobody
    // is told. Announcing it once here flips the whole app to the sign-in screen at the URL
    // the person was already on.
    //
    // Deliberately NOT 403. That means "signed in, and not allowed to do this" — a PM
    // reaching another PM's booking, an admin opening an owner-only pane. Sending those to a
    // sign-in screen would log somebody out for clicking a button they cannot use, and they
    // would come back as the same person and be refused identically.
    if (res.status === 401 && path !== '/api/me') {
      rememberReturnTo()
      window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT))
    }
    throw new ApiError(res.status, e.code, e.message, e.details)
  }
  return data
}

/**
 * Turn anything thrown by `api.*` into a sentence worth showing someone.
 *
 * This exists because the server already says more than the UI was showing. Every failure
 * carries a code and often a `details` payload — who the owners are, which fields were
 * refused, what a destructive switch would remove — and all 34 call sites rendered
 * `err.message` alone, so the useful half was built, sent, and thrown away on arrival.
 *
 * Anything unrecognised falls through to its own message, so a message that was already
 * good is never replaced by a vaguer one.
 */
export function describeError(err, fallback = 'Something went wrong.') {
  if (!err) return fallback
  if (typeof err === 'string') return err
  if (!(err instanceof ApiError)) return err.message || fallback

  if (err.code === 'NETWORK') {
    return 'Could not reach the server. Check your connection — nothing was saved.'
  }
  if (err.status === 401) {
    return 'Your session has ended. Reload the page to sign in again.'
  }
  // A 5xx is not the caller's fault and has no useful message of its own — the server
  // deliberately returns "Internal server error" rather than leaking a stack.
  if (err.status >= 500) {
    return 'The server had a problem with that. Nothing was saved — try again in a moment.'
  }

  // A failure with no message of its own — the ApiError constructor falls back to "HTTP 404",
  // which tells somebody nothing they can act on.
  let message = err.message
  if (!message || /^HTTP \d+$/.test(message)) {
    message = err.status === 404 ? 'That is no longer there — it may have just been deleted.' : fallback
  }

  const d = err.details

  // Naming who CAN do it is the whole point of the owners list the server attaches to an
  // OWNER_ONLY refusal: "contact your administrator" is useless when admins are exactly who
  // is being refused.
  if (Array.isArray(d?.owners) && d.owners.length) {
    message = join(message, `Ask ${d.owners.slice(0, 3).join(' or ')}.`)
  }
  // Someone else saved first. Say so plainly rather than leaving "version conflict".
  if (err.code === 'VERSION_CONFLICT') {
    message = join(message, 'Reload to see their change before editing again.')
  }
  return message
}

/**
 * Two sentences, punctuated. Server messages are written without a full stop — "Only an
 * owner can change these teams" — so appending advice straight onto one produced a run-on.
 */
function join(first, second) {
  const head = /[.!?]$/.test(first.trim()) ? first.trim() : `${first.trim()}.`
  return `${head} ${second}`
}

export const api = {
  get: (p) => request('GET', p),
  post: (p, b) => request('POST', p, b),
  patch: (p, b) => request('PATCH', p, b),
  put: (p, b) => request('PUT', p, b),
  del: (p) => request('DELETE', p),
}
